import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Flag, BadgeCheck, Clock, XCircle, Save } from "lucide-react";
import { useApi, errMsg, fileUrl, specLabel, fmtDate, TERMS, API } from "@/lib/api";
import axios from "axios";
import { useMe } from "@/context/Me";
import { Badges, Stars, PhotoPicker, PageTitle, Tape } from "@/components/bits";

function ReviewList({ reviews, canReport, onReport }) {
  if (!reviews?.length) return <p className="text-sm text-slate-500">Ainda sem avaliações.</p>;
  return (
    <div className="space-y-3">
      {reviews.map((r) => (
        <div key={r.id} className="rounded-xl border border-white/10 p-4" data-testid={`review-${r.id}`}>
          <div className="flex items-center justify-between"><Stars value={r.stars} size={14} /><span className="text-xs text-slate-500">{fmtDate(r.created_at)}</span></div>
          <p className="mt-2 text-sm text-slate-300">{r.comment || "—"}</p>
          <div className="mt-1 flex justify-between text-xs text-slate-500"><span>{r.from_name} · {r.job_title}</span>
            {canReport && !r.reported && <button data-testid={`review-report-${r.id}`} onClick={() => onReport(r.id)} className="hover:text-rose-400"><Flag size={12} className="mr-1 inline" />Denunciar</button>}</div>
        </div>
      ))}
    </div>
  );
}

function ProHeader({ u }) {
  return (
    <div className="panel overflow-hidden">
      <Tape />
      <div className="p-6">
        <div className="flex flex-wrap items-center gap-3"><h1 className="text-4xl font-black uppercase" data-testid="profile-name">{u.full_name}</h1><Badges u={u} size="md" /></div>
        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-slate-400"><Stars value={u.rating_avg} count={u.rating_count || 0} /><span><MapPin size={14} className="mr-1 inline" />{u.district}, {u.city}</span>{u.completed_jobs !== undefined && <span>{u.completed_jobs} serviço(s) concluído(s)</span>}</div>
        <div className="mt-4 flex flex-wrap gap-2">{(u.specialties || []).map((s) => <span key={s} className="rounded-full bg-white/5 px-3 py-1 text-sm">{specLabel(s, u.country)}</span>)}</div>
        {u.bio && <p className="mt-4 whitespace-pre-wrap text-slate-300">{u.bio}</p>}
        {u.portfolio?.length > 0 && <div className="mt-5 grid grid-cols-3 gap-2 sm:grid-cols-4">{u.portfolio.map((p) => <img key={p} src={fileUrl(p)} alt="" className="aspect-square w-full rounded-xl object-cover" />)}</div>}
      </div>
    </div>
  );
}

export function PublicPro() {
  const { id } = useParams();
  const [u, setU] = useState(null);
  useEffect(() => { axios.get(`${API}/pros/${id}`).then((r) => setU(r.data)).catch(() => setU(false)); }, [id]);
  if (u === false) return <div className="p-10 text-center text-slate-400">Profissional não encontrado.</div>;
  if (!u) return null;
  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
      <ProHeader u={u} />
      <div className="panel p-6"><h2 className="mb-4 text-2xl font-bold uppercase">Avaliações</h2><ReviewList reviews={u.reviews} /></div>
    </div>
  );
}

const VSTATE = { approved: [BadgeCheck, "Verificado", "text-emerald-300"], pending: [Clock, "Em análise", "text-amber-300"], rejected: [XCircle, "Recusada", "text-rose-300"], none: [Clock, "Não enviada", "text-slate-400"] };

export function MyProfile() {
  const api = useApi();
  const { me, meta, refresh } = useMe();
  const [f, setF] = useState(null);
  const [rev, setRev] = useState([]);
  const [vdoc, setVdoc] = useState([]);
  const [vself, setVself] = useState([]);
  useEffect(() => { if (me && !f) setF({ bio: me.bio || "", specialties: me.specialties || [], portfolio: me.portfolio || [] }); }, [me]); // eslint-disable-line
  const loadRev = () => me && api.get(`/users/${me.user_id}/reviews`).then((r) => setRev(r.data.reviews)).catch(() => {});
  useEffect(() => { loadRev(); }, [me?.user_id]); // eslint-disable-line
  if (!me || !f) return null;
  const T = TERMS[me.country];
  const isPro = me.profile_type === "pro";
  const vs = me.verification?.status || "none";
  const [VI, vl, vc] = VSTATE[vs];
  const save = async () => { try { await api.patch("/me", f); toast.success("Perfil salvo"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const report = async (id) => { try { await api.post(`/reviews/${id}/report`); toast.success("Denúncia enviada ao Zelobra"); loadRev(); } catch (e) { toast.error(errMsg(e)); } };
  const resubmit = async () => { try { await api.post("/me/verification", { doc_photo: vdoc[0], selfie: vself[0] }); toast.success("Fotos enviadas para análise"); refresh(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <PageTitle eyebrow={isPro ? "Perfil profissional" : "Perfil de cliente"} title={me.full_name}>
        {isPro && vs === "approved" && <a href={`/profissional/${me.user_id}`} className="btn-ghost px-4 py-2 text-sm" data-testid="profile-public-link">Ver perfil público</a>}
      </PageTitle>
      <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
        <div className="space-y-6">
          <div className="panel space-y-2 p-5 text-sm" data-testid="profile-private-data">
            <div className="eyebrow mb-2">Seus dados (privados)</div>
            {[["País", `${T.flag} ${T.name}`], [T.doc, me.country === "BR" ? String(me.doc).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : String(me.doc).replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")], ["Nascimento", (me.birth_date || "").split("-").reverse().join("/")], [T.phone, `${T.prefix} ${me.phone}`], ["Email", me.email], [T.address, `${me.address}, ${me.district}, ${me.city} - ${me.state}`], [T.postal, me.postal_code]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-2"><span className="text-slate-400">{k}</span><span className="text-right">{v}</span></div>
            ))}
            <div className="flex items-center justify-between pt-1"><span className="text-slate-400">Avaliação</span><Stars value={me.rating_avg} count={me.rating_count || 0} size={14} /></div>
          </div>
          {isPro && (
            <div className="panel space-y-3 p-5" data-testid="profile-verification">
              <div className="flex items-center justify-between"><div className="eyebrow">Verificação</div><span className={`flex items-center gap-1 text-sm font-bold ${vc}`}><VI size={16} />{vl}</span></div>
              {vs === "rejected" && (
                <>
                  <p className="text-sm text-slate-400">Envie novas fotos do documento e uma selfie.</p>
                  <div className="flex gap-6"><PhotoPicker testid="reverify-doc" kind="private" max={1} value={vdoc} onChange={setVdoc} label="Documento" /><PhotoPicker testid="reverify-selfie" kind="private" max={1} capture="user" value={vself} onChange={setVself} label="Selfie" /></div>
                  <button data-testid="reverify-submit" className="btn-hazard" disabled={!vdoc.length || !vself.length} onClick={resubmit}>Enviar para análise</button>
                </>
              )}
            </div>
          )}
        </div>
        <div className="space-y-6">
          {isPro && (
            <div className="panel space-y-5 p-5">
              <div className="eyebrow">Perfil público</div>
              <div className="flex flex-wrap gap-2">{(meta?.specialties || []).map((s) => {
                const on = f.specialties.includes(s);
                return <button key={s} data-testid={`profile-spec-${s}`} onClick={() => setF({ ...f, specialties: on ? f.specialties.filter((x) => x !== s) : [...f.specialties, s] })} className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${on ? "border-[#FFC700] bg-[#FFC700] text-[#111317]" : "border-white/15 text-slate-300"}`}>{specLabel(s, me.country)}</button>;
              })}</div>
              <label className="block"><span className="label">Descrição</span><textarea data-testid="profile-bio" className="field min-h-[110px]" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} /></label>
              <div><span className="label">Fotos de trabalhos</span><PhotoPicker testid="profile-portfolio" max={12} value={f.portfolio} onChange={(v) => setF((o) => ({ ...o, portfolio: v }))} /></div>
              <button data-testid="profile-save" className="btn-hazard" onClick={save}><Save size={16} />Salvar perfil</button>
            </div>
          )}
          <div className="panel p-5"><h2 className="mb-4 text-2xl font-bold uppercase">Avaliações recebidas</h2><ReviewList reviews={rev} canReport onReport={report} /></div>
        </div>
      </div>
    </div>
  );
}
